import React, { useCallback, useEffect, useState } from 'react';
import {
  DataGridPro,
  GridColDef,
  GridOverlay,
  GridRowParams,
  GridSortModel,
  GridFilterModel,
  getGridSingleSelectOperators,
} from '@mui/x-data-grid-pro';

import Moment from 'moment/moment';

import ReportRequestButton from './ReportRequestButton';
import { BillingFrequency, billingFrequencyOptions, BillingRecordInvoice } from '../../interfaces/billing.interface';
import { RowActionsMenuProps } from '../../interfaces/datagrid.interface';
import { patchBillingRecord, getBillingRecordsForDataGrid } from '../../services/billingRecords';
import { Card } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { Link } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Invoice,
  BillingRecord,
  BillingRecordPatchObj,
  InvoicePatchPostObj,
} from '../../interfaces/billing.interface';
import InvoiceForm from '../Billing/InvoiceForm';
import StandardDialog, { StandardDialogActions } from '../Modals/StandardDialog';
import Button from '@mui/material/Button';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';

import { patchInvoice, deleteInvoice } from '../../services/invoices';
import { processAdminBillingReportRequest } from '../../services/billingRecords';
import { MoreVert } from '@mui/icons-material';
import { COMPANY_TYPES } from '../../services/vendor';
import BaseToolbar from '../Datagrid/BaseToolbar';
import GridPaginationBar from './GridPaginationBar';
import { normalizeFilterModel, sameModel, stringContainsOnly } from '../../utils/adminDashboardUtils';

enum Status {
  active = 'Active',
  inactive = 'Inactive',
  hold = 'Hold',
}

enum BillingStatus {
  null = '',
  void = 'Void',
  already_invoiced = 'Already Invoiced',
  paid = 'Paid',
  ready_for_invoice = 'Ready for Invoice',
  needs_validation = 'Needs Validation',
  write_off = 'Write Off',
}

enum BillingRecordStatus {
  active = 'Active',
  inactive = 'Inactive',
  hold = 'Hold',
}

enum CompanyType {
  assessor = 'ASSESSOR',
  content_owner = 'CONT OWNER',
  vendor = 'SRV PROVIDER',
}

type ReasonOption = { value: string; label: string };

const INACTIVE_REASON_OPTIONS: ReasonOption[] = [
  { value: 'lapse', label: 'Lapse' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'signed_never_paid', label: 'Signed/Never Paid' },
  { value: 'cancelled_30_days_plus', label: 'Cancelled 30 Days+' },
];

// "Rejoined" documents the inactive -> active transition, so it is only offered on a stored-inactive record.
const REJOINED_REASON_OPTION: ReasonOption = { value: 'rejoined', label: 'Rejoined' };

const DETAIL_FOR_CANCELLATION_OPTIONS: ReasonOption[] = [
  { value: 'no_funds_for_membership', label: 'Do not have funds to pay membership' },
  { value: 'studios_require_own_assessments', label: 'Studios still require own or alternate assessments' },
  { value: 'customers_have_not_requested_tpn', label: 'Customers have not requested TPN' },
  { value: 'change_in_company_status', label: 'Change in company status' },
  { value: 'will_join_later', label: 'Will join later' },
  { value: 'uncollectible', label: 'Uncollectible' },
];

const BillingFrequencyCaptured: { [key in BillingFrequency]: string } = {
  [BillingFrequency.Quarterly]: 'Quarterly',
  [BillingFrequency.Annually]: 'Annually',
  [BillingFrequency.Biennially]: 'Biennially',
};

function getEnumValue(ENUM: any, value: string) {
  const enumValue = ENUM[value as keyof typeof ENUM];
  if (!enumValue) return '';
  return enumValue;
}

function getAddressId(billingRecord: BillingRecord): string {
  if (!billingRecord) return '';
  return 'BILLING';
}

function updateRowsState(
  currentBillingRecordRow: BillingRecord,
  editedInvoice: Invoice,
  setRows: React.Dispatch<React.SetStateAction<BillingRecord[]>>,
  isCreating: boolean = false,
) {
  setRows((prevRows) => {
    const currentRows = [...prevRows];

    const billingRecordIndex = currentRows.findIndex(
      (billingRecord) => billingRecord.id === currentBillingRecordRow.id,
    );

    if (billingRecordIndex === -1) return currentRows;

    const row = currentRows[billingRecordIndex];

    const normalizedInvoice: BillingRecordInvoice = {
      ...(editedInvoice as any),
      billingRecord:
        typeof editedInvoice.billingRecord === 'number'
          ? editedInvoice.billingRecord
          : editedInvoice.billingRecord?.id ?? row.id,
    };

    const updatedInvoices = [...row.billingInvoices];

    const invoiceIndex = updatedInvoices.findIndex(
      (invoice) => invoice.id === normalizedInvoice.id,
    );

    if (invoiceIndex > -1) {
      updatedInvoices[invoiceIndex] = normalizedInvoice;
    } else {
      updatedInvoices.push(normalizedInvoice);
    }

    const isShownInvoice = row.shownInvoice?.id === normalizedInvoice.id;
    const isMostRecentInvoice = row.mostRecentInvoice?.id === normalizedInvoice.id;

    const nextRow: BillingRecord = {
      ...row,
      billingInvoices: updatedInvoices,
    };

    if (isCreating) {
      nextRow.mostRecentInvoice = normalizedInvoice;
    } else {
      nextRow.currentInvoice = normalizedInvoice;
    }

    if (isShownInvoice) nextRow.shownInvoice = normalizedInvoice;
    if (isMostRecentInvoice) nextRow.mostRecentInvoice = normalizedInvoice;

    currentRows[billingRecordIndex] = nextRow;
    return currentRows;
  });
}

const getInvoiceStatusFormField = (row: any) => {
  return (
    <FormControl fullWidth>
      <Select
        labelId="invoiceStatus"
        id="invoice-status-select"
        label="Invoice Status"
        sx={{
          '& fieldset': { border: 'none' },
        }}
        onChange={(e) => {
          const status = e.target.value;
          if (row.shownInvoice) {
            const invoicePatchObject: InvoicePatchPostObj = {
              ...row.shownInvoice,
              sku: row.shownInvoice.sku.id,
              billingRecord: row.id,
              status: status,
            };
            patchInvoice(invoicePatchObject).then((updatedInvoice) => updateRowsState(row, updatedInvoice, row.setRows));
          }
        }}
        value={row.shownInvoice?.status ?? ''}
      >
        <MenuItem value='' selected={row.shownInvoice?.status === ''}></MenuItem>
        <MenuItem value='void' selected={row.shownInvoice?.status === 'void'}>Void</MenuItem>
        <MenuItem value='already_invoiced' selected={row.shownInvoice?.status === 'already_invoiced'}>Already Invoiced</MenuItem>
        <MenuItem value='ready_for_invoice' selected={row.shownInvoice?.status === 'ready_for_invoice'}>Ready For Invoice</MenuItem>
        <MenuItem value='needs_validation' selected={row.shownInvoice?.status === 'needs_validation'}>Needs Validation</MenuItem>
        <MenuItem value='paid' selected={row.shownInvoice?.status === 'paid'}>Paid</MenuItem>
        <MenuItem value='write_off' selected={row.shownInvoice?.status === 'write_off'}>Write Off</MenuItem>
      </Select>
    </FormControl>
  );
};

const getInvoiceIsPaidFormField = (row: any) => {
  return (
    <FormControl fullWidth>
      <Select
        key={row.shownInvoice?.isPaid ? 1 : 2}
        labelId="invoiceIsPaid"
        id="is-paid-select"
        label="Invoice Is Paid"
        sx={{
          '& fieldset': { border: 'none' },
        }}
        onChange={(e) => {
          const isPaid = e.target.value == 1;
          if (row.shownInvoice) {
            const invoicePatchObject: InvoicePatchPostObj = {
              ...row.shownInvoice,
              sku: row.shownInvoice.sku.id,
              billingRecord: row.id,
              isPaid: isPaid,
              paidDate: isPaid ? new Date() : null,
              status: isPaid ? 'paid' : row.shownInvoice.status != 'paid' ? row.shownInvoice.status : 'ready_for_invoice',
            };
            patchInvoice(invoicePatchObject).then((updatedInvoice) => updateRowsState(row, updatedInvoice, row.setRows));
          }
        }}
        value={row.shownInvoice?.isPaid ? 1 : 2}
      >
        <MenuItem value={1}>Yes</MenuItem>
        <MenuItem value={2}>No</MenuItem>
      </Select>
    </FormControl>
  );
};

const InvoiceColumns: GridColDef[] = [
  { field: 'id', headerName: 'ID', width: 80 },
  { field: 'item', headerName: 'Item', width: 150, valueGetter: params => params.row.sku.item },
  { field: 'startDate', headerName: 'Start Date', width: 200, renderCell: params => (<span>{Moment(params.row.startDate).format('M/D/YYYY')}</span>) },
  { field: 'endDate', headerName: 'End Date', width: 200, renderCell: params => (<span>{Moment(params.row.endDate).format('M/D/YYYY')}</span>) },
  { field: 'invoiceStatus', headerName: 'Invoice Status', width: 200, renderCell: params => (<span>{getEnumValue(BillingStatus, params.row.status)}</span>) },
  { field: 'description', headerName: 'Description', width: 200, valueGetter: params => params.row.sku.description },
  { field: 'itemTotal', headerName: 'Item Total', width: 200, valueGetter: params => params.row.sku.itemTotal },
  { field: 'discountAmount', headerName: 'Discount Amount', width: 200 },
  { field: 'discountPercent', headerName: 'Discount Percent', width: 200 },
  { field: 'invoiceTotal', headerName: 'Invoice Total', width: 200 },
  { field: 'poNumber', headerName: 'PO Number', width: 200 },
  { field: 'billingFrequency_captured', headerName: 'Billing Freq. Captured', width: 200, valueGetter: params => getEnumValue(BillingFrequencyCaptured, params.row.billingFrequencyCaptured) },
  { field: 'isPaid', headerName: 'Is Paid', width: 200, renderCell: params => (<span>{params.row.isPaid ? 'Yes' : 'No'}</span>) },
  { field: 'paid_date', headerName: 'Paid Date', width: 200, renderCell: params => (<span>{Moment(params.row.paidDate).format('M/D/YYYY')}</span>) },
  { field: 'notes', headerName: 'Notes', width: 200, renderCell: params => (<span>{params.row.notes}</span>) },
  { field: 'createdAt', headerName: 'Created At', width: 200, renderCell: params => (<span>{Moment(params.row.createdAt).format('M/D/YYYY')}</span>) },
];

export function RowActionsMenu({ anchorEl, handleClose, currentBillingRecordRow, setCurrentBillingRecordRow, fetchAndSetRows, setRows }: RowActionsMenuProps) {
  const open = Boolean(anchorEl);
  const [createInvoiceOpen, setCreateInvoiceOpen] = useState(false);
  const [editInvoiceOpen, setEditInvoiceOpen] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState<Invoice | null>(null);
  const [showDataGrid, setShowDataGrid] = useState(true);

  const handleInvoiceSubmit = (editedInvoice: Invoice, isCreating: boolean = false) => {
    if (!currentBillingRecordRow) return;

    updateRowsState(currentBillingRecordRow, editedInvoice, setRows, isCreating);
    setCreateInvoiceOpen(false);
    setEditInvoiceOpen(false);
    handleClose();
  };

  const handleInvoiceDelete = async (invoiceToDelete: Invoice) => {
    if (!currentBillingRecordRow || !invoiceToDelete) return;

    await deleteInvoice(invoiceToDelete);

    const updatedInvoices = currentBillingRecordRow.billingInvoices.filter(
      (invoice: BillingRecordInvoice) => invoice.id !== invoiceToDelete.id,
    );
    const updatedRow = { ...currentBillingRecordRow, billingInvoices: updatedInvoices };

    setCurrentBillingRecordRow(updatedRow);
    setRows((prevRows: BillingRecord[]) => {
      const currentRows = [...prevRows];
      const billingRecordIndex = currentRows.findIndex(
        (billingRecord) => billingRecord.id === currentBillingRecordRow.id,
      );
      if (billingRecordIndex === -1) return currentRows;

      currentRows[billingRecordIndex] = updatedRow;
      return currentRows;
    });

    setEditInvoiceOpen(false);
    setShowDataGrid(true);
  };

  const handleRowClick = (params: GridRowParams) => {
    setShowDataGrid(false);
    setCurrentInvoice(params.row);
    setEditInvoiceOpen(true);
  };

  const handleEditInvoiceClose = () => {
    setEditInvoiceOpen(false);
    setShowDataGrid(true);
  };

  const handleCreateInvoiceClose = () => {
    setCreateInvoiceOpen(false);
    setShowDataGrid(true);
  };

  const handleCreateInvoiceOpen = () => {
    setCreateInvoiceOpen(true);
    setShowDataGrid(false);
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    clearErrors,
    setError,
    setValue,
  } = useForm({ defaultValues: currentBillingRecordRow ? currentBillingRecordRow : undefined });

  // "Rejoined" is a valid new pick only on a stored-inactive record; also keep it visible when the record already stores it.
  const showRejoinedOption = currentBillingRecordRow?.status === 'inactive' || currentBillingRecordRow?.inactiveReason === 'rejoined';
  const inactiveReasonOptions = showRejoinedOption
    ? [...INACTIVE_REASON_OPTIONS, REJOINED_REASON_OPTION]
    : INACTIVE_REASON_OPTIONS;

  const toDateString = (value: any) => (value && Moment(value).isValid() ? Moment(value).format('YYYY-MM-DD') : null);
  const emptyToNull = (value: any) => value || null;

  const onSubmit = (data: FieldValues) => {
    if (!currentBillingRecordRow?.id) return;

    // The API requires a reason only on the transition into inactive, so mirror just that transition.
    const becomingInactive = currentBillingRecordRow?.status !== 'inactive' && data.status === 'inactive';
    if (becomingInactive && !data.inactiveReason) {
      setError('inactiveReason', { type: 'manual', message: 'An inactive reason is required when marking a billing record inactive.' });
      return;
    }

    const billingRecordData: BillingRecordPatchObj = {
      id: currentBillingRecordRow.id,
      status: data.status,
      notes: data.notes,
      updatedAt: new Date(),
    };

    // Only send reason/date fields that actually changed, so an unrelated edit can't re-send a now-invalid stored value.
    if (emptyToNull(data.inactiveReason) !== emptyToNull(currentBillingRecordRow.inactiveReason)) billingRecordData.inactiveReason = emptyToNull(data.inactiveReason);
    if (emptyToNull(data.detailForCancellation) !== emptyToNull(currentBillingRecordRow.detailForCancellation)) billingRecordData.detailForCancellation = emptyToNull(data.detailForCancellation);
    if (toDateString(data.mostRecentCancellationDate) !== (currentBillingRecordRow.mostRecentCancellationDate ?? null)) billingRecordData.mostRecentCancellationDate = toDateString(data.mostRecentCancellationDate);
    if (toDateString(data.mostRecentRejoinDate) !== (currentBillingRecordRow.mostRecentRejoinDate ?? null)) billingRecordData.mostRecentRejoinDate = toDateString(data.mostRecentRejoinDate);

    clearErrors();

    patchBillingRecord(billingRecordData).then(() => {
      reset();
      fetchAndSetRows();
      handleClose();
    }).catch((error) => {
      const apiErrors = error?.response?.data ?? {};
      Object.keys(apiErrors).forEach((field) => {
        const message = Array.isArray(apiErrors[field]) ? apiErrors[field][0] : apiErrors[field];
        setError(field as any, { type: 'server', message: String(message) });
      });
    });
  };

  useEffect(() => {
    setValue('status', currentBillingRecordRow?.status);
    setValue('inactiveReason', currentBillingRecordRow?.inactiveReason ?? '');
    setValue('detailForCancellation', currentBillingRecordRow?.detailForCancellation ?? '');
    setValue('mostRecentCancellationDate', currentBillingRecordRow?.mostRecentCancellationDate ?? null);
    setValue('mostRecentRejoinDate', currentBillingRecordRow?.mostRecentRejoinDate ?? null);
    setValue('notes', currentBillingRecordRow?.notes);
  }, [currentBillingRecordRow]);

  return (
    <div>
      {currentBillingRecordRow &&
          <StandardDialog title={`${currentBillingRecordRow.company.name} Invoices`} handleClose={() => {
            handleClose();
            setShowDataGrid(true);
            setEditInvoiceOpen(false);
            setCreateInvoiceOpen(false);
          }} isOpen={open}>
            {showDataGrid ?
              <div>
                <Box sx={{ width: '100%' }} component="form" method='patch'>
                  <Grid container spacing={2} alignItems="flex-start" mb={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        disabled
                        id='billingCustomerId'
                        label='Billing Customer ID'
                        sx={{ width: '100%' }}
                        defaultValue={currentBillingRecordRow.billingCustomerId}
                        inputProps={{ maxLength: 100 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        disabled
                        id='billingFrequency'
                        label='Billing Frequency'
                        sx={{ width: '100%' }}
                        value={billingFrequencyOptions.find((option) => option.value === (currentBillingRecordRow.billingFrequency ?? currentBillingRecordRow.company?.billingFrequency))?.key ?? ''}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel id="billingRecordStatus">Billing Record Status</InputLabel>
                        <Select
                          labelId="billingRecordStatus"
                          id="billing-record-status-select"
                          label="Billing Record Status"
                          onChange={(e) => setValue('status', e.target.value)}
                          defaultValue={currentBillingRecordRow.status}
                        >
                          {Object.keys(Status).map((key: string, idx: number) => <MenuItem key={idx} value={key} selected={currentBillingRecordRow?.status === key}>{getEnumValue(Status, key)}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth error={!!errors.inactiveReason}>
                        <InputLabel id="inactiveReason">Inactive Reason</InputLabel>
                        <Select
                          labelId="inactiveReason"
                          id="inactive-reason-select"
                          label="Inactive Reason"
                          onChange={(e) => { clearErrors('inactiveReason'); setValue('inactiveReason', e.target.value); }}
                          defaultValue={currentBillingRecordRow.inactiveReason ?? ''}
                        >
                          <MenuItem value=''><em>None</em></MenuItem>
                          {inactiveReasonOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                        </Select>
                        {errors.inactiveReason && <FormHelperText>{typeof errors.inactiveReason?.message === 'string' ? errors.inactiveReason.message : undefined}</FormHelperText>}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel id="detailForCancellation">Detail for Cancellation</InputLabel>
                        <Select
                          labelId="detailForCancellation"
                          id="detail-for-cancellation-select"
                          label="Detail for Cancellation"
                          onChange={(e) => setValue('detailForCancellation', e.target.value)}
                          defaultValue={currentBillingRecordRow.detailForCancellation ?? ''}
                        >
                          <MenuItem value=''><em>None</em></MenuItem>
                          {DETAIL_FOR_CANCELLATION_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <Controller
                          control={control}
                          name="mostRecentCancellationDate"
                          render={({ field: { onChange, value } }) => (
                            <DesktopDatePicker
                              label="Most Recent Cancellation Date"
                              inputFormat="YYYY-MM-DD"
                              value={value ?? null}
                              onChange={(newValue) => onChange(newValue)}
                              renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <Controller
                          control={control}
                          name="mostRecentRejoinDate"
                          render={({ field: { onChange, value } }) => (
                            <DesktopDatePicker
                              label="Most Recent Rejoin Date"
                              inputFormat="YYYY-MM-DD"
                              value={value ?? null}
                              onChange={(newValue) => onChange(newValue)}
                              renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        error={!!errors.notes}
                        helperText={ typeof errors.notes?.message === 'string' ? errors.notes?.message : undefined }
                        id='notes'
                        label='Notes'
                        sx={{ width: '100%' }}
                        autoFocus
                        multiline
                        rows={4}
                        onFocus={() => clearErrors('notes')}
                        {...register('notes')}
                        inputProps={{ maxLength: 100 }}
                      />
                    </Grid>
                  </Grid>
                </Box>
                {currentBillingRecordRow.billingInvoices.length > 0 && <>
                  <DataGridPro
                      pagination
                      paginationMode="server"
                      rows={currentBillingRecordRow.billingInvoices}
                      onRowClick={handleRowClick}
                      columns={InvoiceColumns}
                      pageSize={5}
                      rowsPerPageOptions={[5, 10, 20]}
                  /></>}
              </div> : <div>
                {editInvoiceOpen && currentInvoice && <InvoiceForm callBack={handleInvoiceSubmit} billingRecord={currentBillingRecordRow} invoice={currentInvoice} handleClose={() => handleEditInvoiceClose()} company={currentBillingRecordRow.company} handleDelete={handleInvoiceDelete} />}
                {createInvoiceOpen && <InvoiceForm callBack={handleInvoiceSubmit} billingRecord={currentBillingRecordRow} invoice={null} handleClose={() => handleCreateInvoiceClose()} company={currentBillingRecordRow.company} handleDelete={handleInvoiceDelete}/>}
              </div>
            }
            {!createInvoiceOpen && !editInvoiceOpen &&
                <StandardDialogActions>
                    <Button
                        color="primary"
                        type='submit'
                        onClick={handleSubmit(onSubmit)}
                    >
                        Update Billing Record
                    </Button>
                  <Button
                      onClick={() => handleCreateInvoiceOpen()}
                      color="primary">
                      Invoice {currentBillingRecordRow.company.name}
                  </Button>
                </StandardDialogActions>
            }
          </StandardDialog>
      }
    </div>
  );
}

function CustomToolbar(props: {
  showCurrentInvoices: boolean;
  toggleShowCurrentInvoices: (v: boolean) => void;
  fetchAndSetRows: () => Promise<void>;
}) {
  const { showCurrentInvoices, toggleShowCurrentInvoices, fetchAndSetRows, ...rest } = props;

  const handleProcessAdminBillingReportRequest = useCallback(async () => {
    try {
      await processAdminBillingReportRequest(showCurrentInvoices);
    } catch (error) {
      console.error('Failed to process admin billing report request:', error);
      throw error;
    }
  }, [showCurrentInvoices]);

  return (
    <BaseToolbar
      {...rest}
      exportExtraItems={
        <ReportRequestButton
          type="Billing Records"
          onRequest={handleProcessAdminBillingReportRequest}
        />
      }>
      <Button
        variant="text"
        size="small"
        startIcon={<ReceiptOutlinedIcon />}
        onClick={() => toggleShowCurrentInvoices(!showCurrentInvoices)}
      >
        Show {showCurrentInvoices ? 'Most Recently Created Invoice' : 'Current Billing Period Invoice'}
      </Button>
    </BaseToolbar>
  );
}

export default function BillingRecordsAdminPanel() {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);
  const [rows, setRows] = useState<BillingRecord[]>([]);
  const [rowSelectionModel, setRowSelectionModel] = useState<(string | number)[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentBillingRecordRow, setCurrentBillingRecordRow] = useState<BillingRecord | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [showCurrentPeriodInvoices, setShowCurrentPeriodInvoices] = useState(true);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModelDraft, setFilterModelDraft] = useState<GridFilterModel>({ items: [] });
  const [filterModelApplied, setFilterModelApplied] = useState<GridFilterModel>({ items: [] });

  const handleMenuClose = (event: any) => {
    if (event) event.preventDefault();
    setMenuAnchorEl(null);
    setCurrentBillingRecordRow(null);
  };

  function handleRowClick(params: GridRowParams, event: any) {
    event.preventDefault();
    setCurrentBillingRecordRow(params.row);
    setMenuAnchorEl(event.currentTarget);
    setRowSelectionModel([params.id]);
  }

  const fetchAndSetRows = useCallback(async () => {
    try {
      setIsLoading(true);
      const { results, count } = await getBillingRecordsForDataGrid({
        page,
        pageSize,
        sortModel,
        filterModel: filterModelApplied,
        showCurrentPeriodInvoices,
      });

      const billingRecords: BillingRecord[] = results.map(
        (billingRecord: BillingRecord, index: number) => ({
          ...billingRecord,
          _rowId: `${billingRecord.id}-${page}-${index}`,
          fetchAndSetRows,
          setRows,
          shownInvoice: showCurrentPeriodInvoices
            ? billingRecord.currentInvoice
            : billingRecord.mostRecentInvoice,
        }),
      );

      setRows(billingRecords);
      setRowCount(count);
    } catch (error) {
      console.error('Error fetching billing records:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, sortModel, filterModelApplied, showCurrentPeriodInvoices]);

  useEffect(() => {
    setIsLoading(true);
    fetchAndSetRows();
  }, [page, pageSize, filterModelApplied, sortModel, showCurrentPeriodInvoices]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;

      setRowSelectionModel([]);
      setMenuAnchorEl(null);
      setCurrentBillingRecordRow(null);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const toggleShowCurrentInvoices = (showCurrentInvoices: boolean) => {
    setShowCurrentPeriodInvoices(showCurrentInvoices);

    setRows(prev =>
      prev.map((billingRecord: BillingRecord) => ({
        ...billingRecord,
        handleRowClick,
        shownInvoice: showCurrentInvoices
          ? billingRecord.currentInvoice
          : billingRecord.mostRecentInvoice,
      })),
    );
  };

  const invoiceStatusOptions = Object.keys(BillingStatus);

  const columns: GridColDef[] = [
    {
      field: 'billing_customer_id',
      headerName: 'Customer ID',
      width: 200,
      filterOperators: stringContainsOnly,
      valueGetter: (params) => params.row.billingCustomerId,
    },
    {
      field: 'updatedAt',
      headerName: 'Updated At',
      width: 200,
      filterOperators: stringContainsOnly,
      valueGetter: (params) => {
        const updatedAt = params.row.shownInvoice?.updatedAt;
        if (!updatedAt) return '';
        return Moment(updatedAt).format('M/D/YYYY');
      },
      renderCell: (params) => (
        <span>{params.value || ''}</span>
      ),
    },
    {
      field: 'invoiceStatus',
      headerName: 'Invoice Status',
      width: 225,
      type: 'singleSelect',
      valueOptions: invoiceStatusOptions,
      filterOperators: getGridSingleSelectOperators(),
      valueGetter: (params) =>
        getEnumValue(BillingStatus, params.row.shownInvoice?.status),
      renderCell: (params) =>
        getInvoiceStatusFormField(params.row),
    },
    {
      field: 'invoiceIsPaid',
      headerName: 'Invoice Is Paid',
      width: 150,
      type: 'singleSelect',
      valueOptions: ['Yes', 'No'],
      filterOperators: getGridSingleSelectOperators(),
      valueGetter: params => (params.row.shownInvoice?.isPaid ? 'Yes' : 'No'),
      renderCell: params => getInvoiceIsPaidFormField(params.row),
    },
    {
      field: 'paidDate',
      headerName: 'Paid Date',
      filterOperators: stringContainsOnly,
      width: 200,
      valueGetter: params => params.row.shownInvoice?.paidDate ? Moment(params.row.shownInvoice?.paidDate).format('M/D/YYYY') : '',
    },
    {
      field: 'company__is_in_grace_period',
      headerName: 'Is In Grace Period',
      type: 'singleSelect',
      valueOptions: ['Yes', 'No'],
      filterOperators: getGridSingleSelectOperators(),
      width: 200,
      valueGetter: params => params.row.company.isInGracePeriod ? 'Yes' : 'No',
    },
    {
      field: 'company__marked_is_in_grace_period_at',
      headerName: 'Marked Is In Grace Period At',
      filterOperators: stringContainsOnly,
      width: 250,
      valueGetter: params => params.row.company?.markedIsInGracePeriodAt && Moment(params.row.company?.markedIsInGracePeriodAt).format('M/D/YYYY'),
    },
    {
      field: 'status',
      headerName: 'Billing Record Status',
      filterOperators: getGridSingleSelectOperators(),
      type: 'singleSelect',
      valueOptions: Object.keys(BillingRecordStatus),
      valueGetter: params => getEnumValue(BillingRecordStatus, params.row.status),
      width: 150,
    },
    {
      field: 'parentCompanyBillingCustomerId',
      headerName: 'Parent Customer ID',
      filterOperators: stringContainsOnly,
      width: 200,
    },
    {
      field: 'company__name',
      headerName: 'Name',
      filterOperators: stringContainsOnly,
      width: 300,
      valueGetter: params => params.row.company.name,
      renderCell: params => (<Link to={`/registry/${params.row.company.id}`}>{params.row.company.name}</Link>),
    },
    {
      field: 'company__created_at',
      headerName: 'Registration Date',
      filterOperators: stringContainsOnly,
      width: 300,
      valueGetter: params => Moment(params.row.company?.createdAt).format('M/D/YYYY'),
    },
    {
      field: 'company__billing_statement_name',
      headerName: 'Statement Name',
      filterOperators: stringContainsOnly,
      width: 300,
      valueGetter: params => params.row.company.billingStatementName,
    },
    {
      field: 'company__billing_contact__name',
      headerName: 'Contact Name',
      filterOperators: stringContainsOnly,
      width: 300,
      valueGetter: params => params.row.billingContact?.name,
    },
    {
      field: 'addressId',
      headerName: 'Address ID',
      filterOperators: stringContainsOnly,
      filterable: false,
      width: 200,
      valueGetter: params => getAddressId(params.row),
    },
    {
      field: 'company__billing_contact__address',
      headerName: 'Address 1',
      filterOperators: stringContainsOnly,
      width: 200,
      valueGetter: params => params.row.billingContact?.address,
    },
    {
      field: 'company__billing_contact__address_2',
      headerName: 'Address 2',
      filterOperators: stringContainsOnly,
      width: 200,
      valueGetter: params => params.row.billingContact?.address2,
    },
    {
      field: 'company__billing_contact__address_3',
      headerName: 'Address 3',
      filterOperators: stringContainsOnly,
      filterable: false,
      width: 200,
      valueGetter: params => params.row.billingContact?.address3,
    },
    {
      field: 'company__vat_number',
      headerName: 'VAT #',
      filterOperators: stringContainsOnly,
      width: 200,
      valueGetter: params => params.row.company?.vatNumber,
    },
    {
      field: 'company__billing_contact__city',
      headerName: 'City',
      filterOperators: stringContainsOnly,
      width: 200,
      valueGetter: params => params.row.billingContact?.city,
    },
    {
      field: 'company__billing_contact__state',
      headerName: 'State',
      filterOperators: stringContainsOnly,
      width: 200,
      valueGetter: params => params.row.billingContact?.state,
    },
    {
      field: 'company__billing_contact__zipcode',
      headerName: 'Zipcode',
      filterOperators: stringContainsOnly,
      width: 200,
      valueGetter: params => params.row.billingContact?.zipcode,
    },
    {
      field: 'company__billing_contact__country',
      headerName: 'Country',
      filterOperators: stringContainsOnly,
      width: 200,
      valueGetter: params => params.row.billingContact?.country,
    },
    {
      field: 'company__billing_frequency',
      headerName: 'Billing Frequency',
      filterOperators: stringContainsOnly,
      width: 200,
      valueGetter: params =>  billingFrequencyOptions.find((option) => option.value === params.row.company.billingFrequency)?.key,
    },
    {
      field: 'company__billing_email_to',
      headerName: 'Billing Email To',
      filterOperators: stringContainsOnly,
      width: 200,
      valueGetter: params => params.row.company.billingEmailTo,
    },
    {
      field: 'company__billing_email_cc',
      headerName: 'Billing Email CC',
      filterOperators: stringContainsOnly,
      width: 200,
      valueGetter: params => params.row.company.billingEmailCc,
    },
    {
      field: 'company__billing_contact__updated_at',
      headerName: 'Billing Contact Last Updated At',
      filterOperators: stringContainsOnly,
      width: 250,
      valueGetter: params => params.row.relatedHistoricalContext.companyBillingContact?.lastUpdated && Moment(params.row.relatedHistoricalContext.companyBillingContact?.lastUpdated).format('M/D/YYYY'),
    },
    {
      field: 'billingContactLastChange',
      headerName: 'Billing Contact Last Change',
      filterOperators: stringContainsOnly,
      filterable: false,
      width: 250,
      valueGetter: params => params.row.relatedHistoricalContext.companyBillingContact?.fieldChanges,
    },
    {
      field: 'company__type',
      headerName: 'Class ID',
      filterOperators: getGridSingleSelectOperators(),
      type: 'singleSelect',
      valueOptions: Object.keys(CompanyType),
      width: 200,
      valueGetter: params => {
        if (params.row.company.type == COMPANY_TYPES.CONTENT_OWNER && params.row.company.contentOwnerLevel == 2) return 'MPA MEMBER';
        return getEnumValue(CompanyType, params.row.company.type);
      },
      renderCell: params => {
        if (params.row.company.type == COMPANY_TYPES.CONTENT_OWNER && params.row.company.contentOwnerLevel == 2) return (<span>MPA MEMBER</span>);
        return (<span>{getEnumValue(CompanyType, params.row.company.type)}</span>);
      },
    },
    {
      field: 'item',
      headerName: 'Item',
      width: 200,
      valueGetter: params => params.row.shownInvoice?.sku?.item,
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      filterOperators: stringContainsOnly,
      width: 200,
      valueGetter: params => params.row.shownInvoice?.startDate && Moment(params.row.shownInvoice?.startDate).format('M/D/YYYY'),
      renderCell: params => (<span>{params.row.shownInvoice?.startDate && Moment(params.row.shownInvoice?.startDate).format('M/D/YYYY')}</span>),
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      filterOperators: stringContainsOnly,
      width: 200,
      valueGetter: params => params.row.shownInvoice?.endDate && Moment(params.row.shownInvoice?.endDate).format('M/D/YYYY'),
      renderCell: params => (<span>{params.row.shownInvoice?.endDate && Moment(params.row.shownInvoice?.endDate).format('M/D/YYYY')}</span>),
    },
    {
      field: 'description',
      headerName: 'Item Description',
      filterOperators: stringContainsOnly,
      width: 200,
      valueGetter: params => params.row.shownInvoice?.sku?.description,
    },
    {
      field: 'itemTotal',
      headerName: 'Item Total',
      filterOperators: stringContainsOnly,
      width: 200,
      valueGetter: params => params.row.shownInvoice?.sku?.itemTotal,
    },
    {
      field: 'discountAmount',
      headerName: 'Discount Amount',
      filterOperators: stringContainsOnly,
      filterable: false,
      width: 200,
      valueGetter: params => params.row.shownInvoice?.discountAmount,
    },
    {
      field: 'invoiceTotal',
      headerName: 'Invoice Total',
      filterOperators: stringContainsOnly,
      width: 200,
      valueGetter: params => params.row.shownInvoice?.invoiceTotal,
    },
    {
      field: 'poNumber',
      headerName: 'PO Number',
      filterOperators: stringContainsOnly,
      width: 200,
      valueGetter: params => params.row.shownInvoice?.poNumber,
    },
    {
      field: 'notes',
      headerName: 'Notes',
      filterOperators: stringContainsOnly,
      width: 200, renderCell: params => (<span>{params.row.shownInvoice?.notes}</span>),
      filterable: false,
    },
    {
      field: 'overflow',
      headerName: '',
      width: 50,
      renderCell: params => (<MoreVert onClick={(e) => params.row.handleRowClick(params, e)} />),
      disableExport: true,
      filterable: false,
    },
  ];

  return (
    <Card sx={{ padding: '2em' }}>
      <DataGridPro
        selectionModel={rowSelectionModel}
        onSelectionModelChange={(model) => setRowSelectionModel(model as (string | number)[])}
        pagination
        paginationMode="server"
        style={{ height: 400, width: '100%' }}
        rows={rows}
        columns={columns}
        getRowId={(row) => row._rowId ?? row.id}
        page={page}
        pageSize={pageSize}
        rowCount={rowCount}
        rowsPerPageOptions={[20, 50]}
        onPageChange={(newPage) => {
          setPage(newPage);
        }}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setPage(0);
        }}
        sortingMode="server"
        onSortModelChange={(model) => {
          setPage(0);
          setSortModel(model);
        }}
        sortModel={sortModel}
        filterMode="server"
        filterModel={filterModelDraft}
        onFilterModelChange={(model) => {
          setPage(0);
          setFilterModelDraft(model);

          const normalized = normalizeFilterModel(model);
          if (!sameModel(normalized, filterModelApplied)) {
            setFilterModelApplied(normalized);
          }
        }}
        onRowClick={handleRowClick}
        loading={isLoading}
        components={{
          Toolbar: CustomToolbar,
          Footer: GridPaginationBar,
          LoadingOverlay: () => (
              <GridOverlay>
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                  <CircularProgress />
                </div>
              </GridOverlay>
          ),
        }}
        componentsProps={{
          toolbar: {
            showCurrentInvoices: showCurrentPeriodInvoices,
            toggleShowCurrentInvoices,
            fetchAndSetRows,
          },
        }}
      />
      <RowActionsMenu
        anchorEl={menuAnchorEl}
        handleClose={(e: any) => handleMenuClose(e)}
        currentBillingRecordRow={currentBillingRecordRow}
        setCurrentBillingRecordRow={setCurrentBillingRecordRow}
        fetchAndSetRows={fetchAndSetRows}
        setRows={setRows}
        rows={rows}
      />
    </Card>
  );
}
